import { Action } from '@fp/interfaces/conversational/generated-async/model/action';
import { ActionBodyDetachService } from '@fp/interfaces/conversational/generated-async/model/action-body-detach-service';
import { ActionBodyDismiss } from '@fp/interfaces/conversational/generated-async/model/action-body-dismiss';
import { ActionBodyPickChoice } from '@fp/interfaces/conversational/generated-async/model/action-body-pick-choice';
import { ActionBodyRead } from '@fp/interfaces/conversational/generated-async/model/action-body-read';
import { ActionPayload } from '@fp/interfaces/conversational/generated-async/model/action-payload';
import { AttachChannel } from '@fp/interfaces/conversational/generated-async/model/attach-channel';
import { AttachChannelResponse } from '@fp/interfaces/conversational/generated-async/model/attach-channel-response';
import { ContextOutgoing } from '@fp/interfaces/conversational/generated-async/model/context-outgoing';
import { MessageIncoming } from '@fp/interfaces/conversational/generated-async/model/message-incoming';
import { MessageOutgoing } from '@fp/interfaces/conversational/generated-async/model/message-outgoing';
import { MessageOutgoingPayload } from '@fp/interfaces/conversational/generated-async/model/message-outgoing-payload';
import { ModelError } from '@fp/interfaces/conversational/generated-async/model/model-error';
import { Participant } from '@fp/interfaces/conversational/generated-async/model/participant';
import { Ping } from '@fp/interfaces/conversational/generated-async/model/ping';
import { Pong } from '@fp/interfaces/conversational/generated-async/model/pong';
import { Publish } from '@fp/interfaces/conversational/generated-async/model/publish';
import { ServiceAttach } from '@fp/interfaces/conversational/generated-async/model/service-attach';
import { StartConversation } from '@fp/interfaces/conversational/generated-async/model/start-conversation';
import { StartConversationResponse } from '@fp/interfaces/conversational/generated-async/model/start-conversation-response';
import { Subscribe } from '@fp/interfaces/conversational/generated-async/model/subscribe';
import { UI } from '@fp/interfaces/conversational/generated-async/model/ui';
import { UIBodyLoading } from '@fp/interfaces/conversational/generated-async/model/ui-body-loading';
import { UIPayload } from '@fp/interfaces/conversational/generated-async/model/ui-payload';
import { ChatModelMapper } from '@klant/conversational-connector/mappers/chat-model-mapper/chat-model.mapper';
import { WebsocketMapper } from '@klant/conversational-connector/mappers/websocket-mapper/websocket.mapper';
import { ChatMessageModel } from '@klant/conversational-connector/models/ChatMessage.model';
import { ConversationModel } from '@klant/conversational-connector/models/Conversation.model';

import { inject, Injectable, signal } from '@angular/core';
import { retry, RetryConfig, Subject } from 'rxjs';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';

import { ApWindowService } from '@ap/cdk';

import {
    ConversationalChatConnectionError,
    MessageModel,
    PickChoiceAction,
    Platform,
    WebsocketResponse
} from '../../klant-conversational-connector.model';

import { CloseSocketEvent } from './websocket.model';

@Injectable({
    providedIn: 'root'
})
export class WebsocketService {
    private readonly apWindowService = inject(ApWindowService);

    public subject: WebSocketSubject<Subscribe | Publish> | undefined;
    public readonly connectionErrorSubject = new Subject<ConversationalChatConnectionError>();
    public readonly connectionSubjectResponse = new Subject<WebsocketResponse | null>();
    public readonly messageLoading = signal<boolean>(false);
    public readonly isAlive = signal(false);

    private readonly missedPongs = signal(0);

    private readonly heartBeatTimer = signal<number | null>(null);
    private readonly MAX_MISSED_PONGS = 2;
    private readonly HEARTBEAT_INTERVAL = 30000;

    public retryConfig: RetryConfig = {
        delay: 2000
    };

    public connect(baseSocketUrl: string, openObserverNext: () => void): void {
        this.clearHeartBeat();

        this.subject = webSocket({
            url: baseSocketUrl,
            openObserver: {
                next: () => {
                    openObserverNext();
                    this.isAlive.set(true);
                    this.missedPongs.set(0);
                    this.setHeartBeat();
                }
            },
            closeObserver: {
                next: this.websocketCloseErrorCheck.bind(this)
            }
        });
    }

    public setHeartBeat(): void {
        this.clearHeartBeat();

        this.subject?.next({ type: Ping.TypeEnum.Ping });

        const heartBeatTimer = this.apWindowService.getWindow().setTimeout(() => {
            if (!this.isAlive()) {
                this.missedPongs.update((pongs) => pongs + 1);

                if (this.missedPongs() >= this.MAX_MISSED_PONGS) {
                    this.clearHeartBeat();
                    this.connectionErrorSubject.next('connection error');
                    this.subject?.complete();
                    return;
                }
            }

            this.isAlive.set(false);
            this.setHeartBeat();
        }, this.HEARTBEAT_INTERVAL);
        this.heartBeatTimer.set(heartBeatTimer);
    }

    public clearHeartBeat(): void {
        const heartBeatTimer = this.heartBeatTimer();
        if (heartBeatTimer !== null) {
            this.apWindowService.getWindow().clearTimeout(heartBeatTimer);
            this.heartBeatTimer.set(null);
        }
    }

    public websocketCloseErrorCheck(closeSocketEvent: CloseSocketEvent): void {
        this.clearHeartBeat();
        this.isAlive.set(false);

        if (closeSocketEvent.code === 1000 && closeSocketEvent.wasClean) {
            return;
        }

        if (closeSocketEvent.code > 1000 && closeSocketEvent.code <= 1015) {
            this.connectionErrorSubject.next('retry connection error');
            return;
        }

        this.connectionErrorSubject.next('connection error');
    }

    public attachSocketChannel(conversationResponse: ConversationModel): void {
        this.subject?.next({
            type: 'attach_channel',
            payload: {
                accessToken: conversationResponse.accessToken,
                channelName: 'api'
            }
        } as AttachChannel);
    }

    public subscribeSocket(): void {
        this.subject?.pipe(retry(this.retryConfig)).subscribe({
            next: (response: Subscribe | Publish) => {
                switch (response.type) {
                    case AttachChannelResponse.TypeEnum.AttachChannelResponse:
                        this.connectionSubjectResponse.next(WebsocketMapper.mapAttachChannelResponseToAttachChannelModel(response));
                        break;
                    case StartConversationResponse.TypeEnum.StartConversationResponse:
                        this.connectionSubjectResponse.next(WebsocketMapper.mapStartConversationResponseToStartConversationModel());
                        break;
                    case Pong.TypeEnum.Pong:
                        this.isAlive.set(true);
                        this.missedPongs.set(0);
                        break;
                    case MessageIncoming.TypeEnum.Message:
                        const chatMessageModel = ChatModelMapper.mapMessageToChatMessageModel(response as MessageIncoming);
                        this.connectionSubjectResponse.next(chatMessageModel);
                        break;
                    case ServiceAttach.TypeEnum.ServiceAttach:
                        const serviceAttachModel = WebsocketMapper.mapServiceAttachToServiceAttachModel(response);
                        this.connectionSubjectResponse.next(serviceAttachModel);
                        break;
                    case UI.TypeEnum.Ui:
                        this.handleSocketUIEvents(response);
                        break;
                    case Participant.TypeEnum.Participant:
                        if (!response.payload.participant.introduction) {
                            break;
                        }
                        const participantModel = ChatModelMapper.mapParticipantPayloadToParticipantMessageModel(response.payload);
                        this.connectionSubjectResponse.next(participantModel);
                        break;
                    default:
                        if (response.type === ModelError.TypeEnum.Error) {
                            this.connectionErrorSubject.next('retry connection error');
                        }
                        break;
                }
            },
            error: () => {
                this.connectionErrorSubject.next('retry connection error');
            },
            complete: () => {
                this.isAlive.set(false);
            }
        });
    }

    public disconnect(): void {
        this.clearHeartBeat();
        this.subject?.unsubscribe();
    }

    public startConversation(): void {
        this.subject?.next({ type: StartConversation.TypeEnum.StartConversation });
    }

    public sendMessage(payload: MessageModel): void {
        const messageToSend: MessageOutgoing = {
            type: MessageOutgoing.TypeEnum.Message,
            payload: {
                type: MessageOutgoingPayload.TypeEnum.Text,
                transactionId: payload.transactionId,
                body: {
                    text: payload.text
                }
            }
        };

        this.subject?.next(messageToSend);
    }

    public markMessagesRead(messages: ChatMessageModel[]) {
        this.subject?.next({
            type: Action.TypeEnum.Action,
            payload: {
                type: ActionBodyRead.TypeEnum.Read,
                events: messages.map((message) => message.id!)
            }
        });
    }

    public sendPickChoiceAction(action: PickChoiceAction): void {
        const payload: ActionPayload = {
            type: ActionBodyPickChoice.TypeEnum.PickChoice,
            originMessage: action.originMessage,
            transactionId: action.transactionId,
            choice: {
                chooseAgain: false,
                id: action.choiceOptionId
            }
        };

        this.subject?.next({
            type: Action.TypeEnum.Action,
            payload
        });
    }

    public continueConversationAfterIdle(): void {
        this.subject?.next({
            type: Action.TypeEnum.Action,
            payload: {
                type: ActionBodyDismiss.TypeEnum.Dismiss,
                body: {
                    type: 'resume_conversation_prompt'
                }
            }
        });
    }

    public endConversationAfterIdle(): void {
        this.subject?.next({
            type: Action.TypeEnum.Action,
            payload: {
                type: ActionBodyDetachService.TypeEnum.DetachService
            }
        });
    }

    public setSeamlyContextVariables(platform: Platform, environment: string): void {
        this.subject?.next({
            type: ContextOutgoing.TypeEnum.Context,
            payload: {
                variables: {
                    channel: platform,
                    environment: environment
                }
            }
        });
    }

    private handleSocketUIEvents(uiEvent: UI): void {
        if (uiEvent.payload.type === UIPayload.TypeEnum.Loading) {
            this.messageLoading.set((uiEvent.payload.body as UIBodyLoading).loading);
        }
    }
}
